import { z } from "zod";

import {
  basicValidator,
  htmlStringValidator,
  phoneNumberValidator,
} from "@/lib/validators";

export enum TicketType {
  INVOICE = "INVOICE",
  DOCKET = "DOCKET",
  GENERAL = "GENERAL",
}

export const baseSupportTicketSchema = z.object({
  projectId: z.string().uuid().optional(),
  type: z.nativeEnum(TicketType),
  description: htmlStringValidator,
});

export const generalSupportTicketSchema = baseSupportTicketSchema.extend({
  documents: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
});

export const updateGeneralSupportTicketSchema =
  generalSupportTicketSchema.extend({
    id: z.string().uuid(),
  });

export const issueSupportTicketSchema = baseSupportTicketSchema.extend({
  invoiceId: z.string().uuid().optional(),
  docketId: z.string().uuid().optional(),
  docketOrInvoiceNumber: z.string().optional(),
});

export const updateIssueSupportTicketSchema = issueSupportTicketSchema.extend({
  id: z.string().uuid(),
});

export const generalSupportTickeDefaultValues = {
  projectId: undefined,
  type: TicketType.GENERAL,
  description: "",
  documents: [],
};

export const issueSupportTicketDefaultValues = {
  projectId: undefined,
  type: TicketType.INVOICE,
  description: "",
  invoiceId: undefined,
  docketId: undefined,
};

export const createTeamContactSchema = z.object({
  name: basicValidator(true, "Required"),
  email: z
    .string()
    .email({
      message: "Invalid Email",
    })
    .min(1, {
      message: "Required",
    }),
  phoneNumber: phoneNumberValidator(true, "Required"),
  position: basicValidator(true, "Required"),
});

export const createImportantDocument = z.object({
  documents: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string()),
    )
    .min(1, {
      message: "Required",
    }),
  name: basicValidator(true, "Required"),
});

export const updateTeamContactSchema = createTeamContactSchema.extend({
  id: z.string().uuid(),
});

export const updateImportantDocument = createImportantDocument
  .partial()
  .extend({
    id: z.string().uuid(),
  });

export const emptyTeamContact = {
  name: undefined,
  email: undefined,
  phoneNumber: undefined,
  position: undefined,
};

export const emptyImportantDocument = {
  name: undefined,
  documents: [],
};

export type GeneralSupportTicket = z.infer<typeof generalSupportTicketSchema>;
export type IssueSupportTicket = z.infer<typeof issueSupportTicketSchema>;
export type UpdateSupportTicket = z.infer<
  typeof updateGeneralSupportTicketSchema
>;

export type UpdateIssueSupportTicket = z.infer<
  typeof updateIssueSupportTicketSchema
>;

export type CreateTeamContact = z.infer<typeof createTeamContactSchema>;
export type UpdateTeamContact = z.infer<typeof updateTeamContactSchema>;
export type CreateImportantDocument = z.infer<typeof createImportantDocument>;
export type UpdateImportantDocument = z.infer<typeof updateImportantDocument>;
